import { Flex, HStack, Text, useColorModeValue } from '@chakra-ui/react';
import React, { useMemo, useCallback } from 'react';

import { DataPipeline } from '@finley-technologies/finley-types';

import { colors } from 'packages/common-frontend/colors';
import { Icon as FaIcon } from 'packages/common-frontend/components/icons';
import { Select } from 'packages/common-frontend/components/Select';
import { useSlugToName } from 'packages/dataPipeline/hooks/slugToNameContext';
// import { SectionHeader } from 'packages/dataPipeline/ui/components/SectionHeader';

export const MeshSelect = ({
  onMeshSelected,
  meshDocuments,
  selectedMeshDocumentId,
  allowSelection,
}: {
  allowSelection: boolean;
  selectedMeshDocumentId: string | null;
  meshDocuments: DataPipeline.MeshDocument[];
  onMeshSelected: (meshDocument: DataPipeline.MeshDocument) => void;
}) => {
  const { cfSlugToName, lpSlugToName } = useSlugToName();
  const enabledMeshDocuments = useMemo(
    () =>
      meshDocuments
        .map((meshDocument) => ({
          id: meshDocument.meshDocumentId,
          cfSlugs: meshDocument.cfSlugs.map(cfSlugToName).join(', '),
          lpSlugs: meshDocument.lpSlugs.map(lpSlugToName).join(', '),
        }))
        .filter(({ id }) => allowSelection || id === selectedMeshDocumentId)
        .sort((a, b) => (a.lpSlugs < b.lpSlugs ? -1 : 1)), // sort by loan product
    [
      allowSelection,
      cfSlugToName,
      lpSlugToName,
      meshDocuments,
      selectedMeshDocumentId,
    ],
  );

  const iconColor = useColorModeValue(colors.black[70], colors.white[700]);

  const selectDisabled = !allowSelection || enabledMeshDocuments.length < 2;

  const handleChange = useCallback(
    (value?: string) => {
      if (value) {
        const selectedMeshDocument = meshDocuments.find(
          (m) => m.meshDocumentId === value,
        );
        if (selectedMeshDocument) {
          onMeshSelected(selectedMeshDocument);
        }
      }
    },
    [meshDocuments, onMeshSelected],
  );

  return (
    <Flex height="100%" alignItems="center" width="100%">
      {enabledMeshDocuments.length > 0 && (
        <Select
          value={selectedMeshDocumentId ?? undefined}
          options={enabledMeshDocuments.map(({ id, cfSlugs }) => ({
            value: id,
            label: (
              <HStack>
                <FaIcon icon="building-columns" color={iconColor} />
                <Text textOverflow="ellipsis">{cfSlugs}</Text>
              </HStack>
            ),
          }))}
          onChange={handleChange}
          isDisabled={selectDisabled}
        />
      )}
    </Flex>
  );
};
